(function (window, document) {
  'use strict';

  if (window.BDMetaPixel) {
    return;
  }

  var PIXEL_ID = '345591407342830';
  var REGISTRATION_MARKER_KEY = 'bd:masterclass-registration:v1';
  var REGISTRATION_MARKER_MAX_AGE = 30 * 60 * 1000;
  var pendingEvents = [];
  var consentApiReady = false;
  var consentListenerBound = false;
  var pixelInitialized = false;
  var pageViewSent = false;
  var queuedRegistrationEventId = null;
  // These storage names are a receipt ledger, not the current Meta event name.
  // Keep them across the CompleteRegistration change to avoid repeat sends.
  var LEAD_PENDING_KEY = 'bd:giveaway-leads:v1';
  var LEAD_MAX_AGE = 30 * 60 * 1000;
  var LEAD_RETRY_DELAY = 31000;
  var leadRetryTimer;
  var pendingLeads = Object.create(null);
  var sentLeadEventIds = Object.create(null);

  function leadAlreadySent(eventId) {
    if (sentLeadEventIds[eventId]) return true;
    try {
      return window.sessionStorage.getItem('bd:giveaway-lead:' + eventId) === '1';
    } catch (error) {
      return false;
    }
  }

  function validLead(marker) {
    var age = marker && Date.now() - marker.createdAt;
    return marker && typeof marker.createdAt === 'number' && typeof marker.entryId === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(marker.entryId)
      && typeof marker.requestId === 'string' && /^[A-Za-z0-9_-]{16,100}$/.test(marker.requestId)
      && Number.isFinite(age) && age >= 0 && age < LEAD_MAX_AGE;
  }

  function persistPendingLeads() {
    var markers = Object.keys(pendingLeads).map(function (id) {
      var lead = pendingLeads[id];
      return { entryId: lead.entryId, requestId: lead.requestId, createdAt: lead.createdAt };
    });
    try {
      if (markers.length) window.sessionStorage.setItem(LEAD_PENDING_KEY, JSON.stringify(markers));
      else window.sessionStorage.removeItem(LEAD_PENDING_KEY);
    } catch (error) { /* In-memory recovery remains available in this page. */ }
  }

  function rememberLead(marker) {
    if (!validLead(marker) || leadAlreadySent('giveaway-' + marker.entryId) || pendingLeads[marker.entryId]) return;
    pendingLeads[marker.entryId] = {
      entryId: marker.entryId, requestId: marker.requestId, createdAt: marker.createdAt,
      attempts: 0, nextAttemptAt: 0, inFlight: false
    };
  }

  function restorePendingLeads() {
    try {
      var markers = JSON.parse(window.sessionStorage.getItem(LEAD_PENDING_KEY) || '[]');
      if (Array.isArray(markers)) markers.slice(0, 20).forEach(rememberLead);
    } catch (error) { /* Ignore corrupt or unavailable browser storage. */ }
    persistPendingLeads();
  }

  function scheduleLeadRetry() {
    if (leadRetryTimer !== undefined) return;
    leadRetryTimer = window.setTimeout(function () {
      leadRetryTimer = undefined;
      flushLeadEvents();
    }, LEAD_RETRY_DELAY);
  }

  async function sendGiveawayLead(lead) {
    // This proof belongs to a completed server entry. The channel remains the
    // sole browser-pixel owner; no fbq/library/init call is made for giveaways.
    if (!consentApiReady || !marketingAllowed() || typeof window.fetch !== 'function' || typeof window.AbortController !== 'function') return;
    lead.inFlight = true;
    lead.attempts += 1;
    var controller = new window.AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, 12000);
    try {
      var response = await window.fetch('/apps/tools/giveaway-lead', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ entryId: lead.entryId, requestId: lead.requestId, marketingAllowed: true,
          userAgent: window.navigator && window.navigator.userAgent ? window.navigator.userAgent : '' }),
        signal: controller.signal
      });
      var payload = await response.json();
      if (response.ok && payload && payload.ok && payload.result && payload.result.accepted === true) {
        var eventId = 'giveaway-' + lead.entryId;
        sentLeadEventIds[eventId] = true;
        try { window.sessionStorage.setItem('bd:giveaway-lead:' + eventId, '1'); } catch (error) { /* Optional persistence. */ }
        delete pendingLeads[lead.entryId];
      } else if ((response.ok && payload && payload.ok && payload.result && payload.result.accepted === false && payload.result.reason === 'not_qualified')
        || (response.status >= 400 && response.status < 500 && response.status !== 429)) {
        // Ineligible, malformed, unknown or expired proof cannot recover by resending it.
        // Dropping pending proof is not a sent marker or a claimed conversion.
        delete pendingLeads[lead.entryId];
      }
    } catch (error) { /* A failed or uncertain acknowledgement remains pending. */ }
    finally {
      window.clearTimeout(timeout);
      lead.inFlight = false;
      lead.nextAttemptAt = Date.now() + LEAD_RETRY_DELAY;
      persistPendingLeads();
      if (pendingLeads[lead.entryId] && lead.attempts < 4) scheduleLeadRetry();
    }
  }

  function flushLeadEvents() {
    Object.keys(pendingLeads).forEach(function (id) {
      if (!validLead(pendingLeads[id])) delete pendingLeads[id];
    });
    persistPendingLeads();
    if (!consentApiReady || !marketingAllowed()) return;
    Object.keys(pendingLeads).forEach(function (id) {
      var lead = pendingLeads[id];
      if (lead.inFlight || lead.attempts >= 4) return;
      if (Date.now() < lead.nextAttemptAt) scheduleLeadRetry();
      else sendGiveawayLead(lead);
    });
  }

  function clearRegistrationMarker() {
    try {
      window.sessionStorage.removeItem(REGISTRATION_MARKER_KEY);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }

  function readRegistrationMarker() {
    var rawMarker;

    try {
      rawMarker = window.sessionStorage.getItem(REGISTRATION_MARKER_KEY);
    } catch (error) {
      return null;
    }

    if (!rawMarker) {
      return null;
    }

    try {
      var marker = JSON.parse(rawMarker);
      var age = Date.now() - marker.createdAt;
      var hasValidEventId = typeof marker.eventId === 'string'
        && marker.eventId.length > 0
        && marker.eventId.length <= 128;

      if (!hasValidEventId || !Number.isFinite(age) || age < 0 || age > REGISTRATION_MARKER_MAX_AGE) {
        clearRegistrationMarker();
        return null;
      }

      return marker;
    } catch (error) {
      clearRegistrationMarker();
      return null;
    }
  }

  function installPixelLibrary() {
    if (window.fbq) {
      return;
    }

    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  }

  function ensurePixel() {
    installPixelLibrary();

    if (!pixelInitialized) {
      window.fbq('init', PIXEL_ID);
      pixelInitialized = true;
    }

    return window.fbq;
  }

  function marketingAllowed() {
    if (!window.Shopify || window.Shopify.designMode) {
      return false;
    }

    var customerPrivacy = window.Shopify.customerPrivacy;
    return customerPrivacy
      && typeof customerPrivacy.marketingAllowed === 'function'
      && customerPrivacy.marketingAllowed() === true;
  }

  function sendEvent(event) {
    var fbq = ensurePixel();

    if (event.type === 'PageView') {
      if (pageViewSent) {
        return;
      }

      pageViewSent = true;
      fbq('trackSingle', PIXEL_ID, 'PageView');
      return;
    }

    if (event.type === 'CompleteRegistration') {
      var marker = readRegistrationMarker();
      if (!marker || marker.eventId !== event.eventId) {
        return;
      }

      fbq('trackSingle', PIXEL_ID, 'CompleteRegistration', {}, { eventID: event.eventId });
      clearRegistrationMarker();
      return;
    }

  }

  function flushPendingEvents() {
    flushLeadEvents();
    if (!consentApiReady || !marketingAllowed()) {
      return;
    }

    while (pendingEvents.length) {
      sendEvent(pendingEvents.shift());
    }
  }

  function isGiveawayReceiptEvent(event) {
    // Cached pages still enqueue Lead. New CompleteRegistration events carry
    // receipt fields; malformed receipt proof must not consume a masterclass
    // marker. Marker-only registrations retain the existing webinar owner.
    return event.type === 'Lead' || (event.type === 'CompleteRegistration' &&
      (Object.prototype.hasOwnProperty.call(event, 'entryId') || Object.prototype.hasOwnProperty.call(event, 'requestId')));
  }

  function enqueue(event) {
    if (!event || typeof event.type !== 'string') {
      return;
    }

    if (isGiveawayReceiptEvent(event)) {
      rememberLead({ entryId: event.entryId, requestId: event.requestId, createdAt: Date.now() });
      persistPendingLeads();
    } else if (event.type === 'PageView') {
      pendingEvents.push({ type: 'PageView' });
    } else if (event.type === 'CompleteRegistration') {
      var marker = readRegistrationMarker();
      if (!marker || marker.eventId === queuedRegistrationEventId) {
        return;
      }

      queuedRegistrationEventId = marker.eventId;
      pendingEvents.push({
        type: 'CompleteRegistration',
        eventId: marker.eventId
      });
    } else {
      return;
    }

    flushPendingEvents();
  }

  function loadConsentApi() {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) {
          return;
        }

        consentApiReady = true;

        if (!consentListenerBound) {
          document.addEventListener('visitorConsentCollected', flushPendingEvents);
          consentListenerBound = true;
        }

        flushPendingEvents();
      }
    );
  }

  var queuedEvents = Array.isArray(window.BDMetaPixelQueue)
    ? window.BDMetaPixelQueue.slice()
    : [];

  window.BDMetaPixel = {
    enqueue: enqueue
  };
  window.BDMetaPixelQueue = {
    push: function () {
      Array.prototype.forEach.call(arguments, enqueue);
    }
  };

  restorePendingLeads();
  queuedEvents.forEach(enqueue);
  loadConsentApi();
})(window, document);
